// Form-specific authentication hooks
import { useState, useCallback } from "react";
import { useLogin, useVerifyOTP } from "../api/auth";
import { LoginFormSchema, OTPFormSchema } from "../model/schemas";
/**
 * Hook for login form logic
 */
export function useLoginForm() {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [phoneNumber, setPhoneNumber] = useState("");
    const { login } = useLogin();
    const handleSubmit = useCallback(async (data) => {
        setIsLoading(true);
        setError(null);
        try {
            // Validate form data
            const validatedData = LoginFormSchema.parse(data);
            // Send OTP
            await login(validatedData.phoneNumber);
            setPhoneNumber(validatedData.phoneNumber);
            return {
                success: true,
                phoneNumber: validatedData.phoneNumber,
            };
        }
        catch (err) {
            const errorMessage = err instanceof Error ? err.message : "Failed to send OTP";
            setError(errorMessage);
            return { success: false, error: errorMessage };
        }
        finally {
            setIsLoading(false);
        }
    }, [login]);
    const reset = useCallback(() => {
        setError(null);
        setPhoneNumber("");
    }, []);
    return {
        handleSubmit,
        isLoading,
        error,
        phoneNumber,
        reset,
    };
}
/**
 * Hook for OTP verification form logic
 */
export function useOTPForm(phoneNumber) {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const { verifyOTP } = useVerifyOTP();
    const handleSubmit = useCallback(async (data) => {
        setIsLoading(true);
        setError(null);
        try {
            // Validate form data
            const validatedData = OTPFormSchema.parse(data);
            // Verify OTP
            const response = await verifyOTP(phoneNumber, validatedData.code);
            return { success: true, user: response.user };
        }
        catch (err) {
            const errorMessage = err instanceof Error ? err.message : "Failed to verify OTP";
            setError(errorMessage);
            return { success: false, error: errorMessage };
        }
        finally {
            setIsLoading(false);
        }
    }, [phoneNumber, verifyOTP]);
    const reset = useCallback(() => {
        setError(null);
    }, []);
    return {
        handleSubmit,
        isLoading,
        error,
        reset,
    };
}
